import React from 'react';
import parsnipNavIcon from '@/assets/icons/brand/parsnip_navigation.svg';

interface MenuItem {
  label: string;
  href?: string;
  icon: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
  external?: boolean;
  id?: string; // Optional unique identifier for menu items
}

interface MenuSection {
  items: MenuItem[];
  showDivider?: boolean;
}

interface ProfileMenuProps {
  showDropdown: boolean;
  sections: MenuSection[];
  onLogout: () => void;
  isLoggingOut?: boolean;
}

const DashboardIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const VideoIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const LogoutIcon: React.FC<{ className?: string }> = ({ className = "h-4 w-4 text-gray-500" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const ArchiveIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

/**
 * ProfileMenu - Common dropdown menu component for user profile actions
 * Used in both Dashboard and Admin headers
 */
const ProfileMenu: React.FC<ProfileMenuProps> = ({
  showDropdown,
  sections,
  onLogout,
  isLoggingOut = false
}) => {
  if (!showDropdown) return null;

  return (
    <div 
      className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50"
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="profile-menu-button"
    >
      <div className="py-1" role="none">
        {sections.map((section) => {
          // Generate section key from first item label or a unique identifier
          const sectionKey = section.items[0]?.label || `section-${section.items.length}`;
          const isLastSection = sections.indexOf(section) === sections.length - 1;
          
          return (
            <React.Fragment key={sectionKey}>
              {section.items.map((item) => {
                const itemKey = item.id || `${item.label}-${item.href || 'action'}`;
                
                if (item.href) {
                  return (
                    <a
                      key={itemKey}
                      href={item.href}
                      role="menuitem"
                      {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className={`w-full text-left px-4 py-2 text-sm ${
                        item.active
                          ? 'text-blue-600 bg-blue-50' 
                          : 'text-gray-700 hover:bg-gray-50'
                      } flex items-center space-x-2`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </a>
                  );
                }
                
                return (
                  <button
                    key={itemKey}
                    onClick={item.onClick}
                    role="menuitem"
                    type="button"
                    className="w-full bg-white text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                );
              })}
              {section.showDivider && !isLastSection && (
                <hr className="border-t border-gray-100 my-1" />
              )}
            </React.Fragment>
          );
        })}
        
        <hr className="border-t border-gray-100 my-1" />
        <button
          onClick={onLogout}
          disabled={isLoggingOut}
          role="menuitem"
          type="button"
          className="w-full bg-white text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <LogoutIcon className="h-4 w-4 text-gray-500" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};

/**
 * Generates profile menu sections configuration
 * @param isAdmin - Whether the user is an admin
 * @param currentPath - Current location pathname
 * @returns Array of menu sections
 */
export const getProfileMenuSections = (isAdmin: boolean, currentPath: string) => [
  {
    items: [
      {
        label: 'Content Creation',
        href: '/dashboard',
        icon: <DashboardIcon />,
        active: currentPath === '/dashboard'
      },
      ...(isAdmin ? [{
        label: 'Admin Dashboard',
        href: '/admin',
        icon: <UsersIcon />,
        active: currentPath === '/admin'
      }] : []),
      {
        label: 'Content Review',
        href: '/content-review',
        icon: <VideoIcon />,
        active: currentPath === '/content-review' || currentPath === '/admin/content-review'
      },
      {
        label: 'Parsnip',
        href: '/parsnip',
        icon: (
        <img
          src={parsnipNavIcon}
          alt="Parsnip icon"
          className="w-5 h-5 contrast-200 brightness-125 drop-shadow-md"
        />
      ),
        active: currentPath === '/parsnip'
      },
      // Commented out Archives menu item
      // ...(isAdmin ? [{
      //   label: 'Archives',
      //   href: '/archives',
      //   icon: <ArchiveIcon />,
      //   active: currentPath === '/archives'
      // }] : []),
      {
        label: 'Test Environment',
        href: 'https://testingpicasso.netlify.app/login',
        icon: <DashboardIcon />,
        external: true
      }
    ],
    showDivider: false
  }
];

export default ProfileMenu;
export { DashboardIcon, VideoIcon, UsersIcon, LogoutIcon, ArchiveIcon };
